/* eslint-disable react/no-unused-prop-types */
import React, { useCallback, useContext } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import InputWithDropdown from './InputWithDropdown'
import { styles } from './styles'
import List from '../List'
import { FocusContext } from '../../Expanded/FocusContext'

const RecipientSearch = React.forwardRef((props, ref) => {
  const { className, ...rest } = props
  const {
    onRemove,
    recipients,
    allowMultipleRecipients,
    editing,
    type,
    query,
    currentRecipientSyncSource,
  } = rest
  const showSingleInput = !allowMultipleRecipients && editing
  const handleOnRemove = useCallback(
    recipient => {
      onRemove(recipient, type, recipients)
    },
    [onRemove, type, recipients]
  )

  const { getFocusableRef, setFocus } = useContext(FocusContext)

  const focus = useCallback(
    () => {
      setFocus(type)
    },
    [setFocus, type]
  )

  return (
    <div
      className={cn('grui flex flex-auto', className)}
      css={styles.recipientSearchContainer}
    >
      <div
        css={styles.recipientSearch}
        className={className}
        tabIndex={editing ? -1 : props.tabIndex || 0}
        onClick={focus}
        onFocus={focus}
        ref={getFocusableRef(type)}
      >
        <div className={'row_content'}>
          <div className={'grui flex recipients'}>
            {showSingleInput && (
              <InputWithDropdown
                {...rest}
                defaultValue={query}
                ref={ref}
                key={`input-dropdown-${type}`}
              />
            )}
            {!showSingleInput && (
              <List
                className="recipients_list"
                onRemove={handleOnRemove}
                recipients={recipients}
                syncing={currentRecipientSyncSource === 'to' && type === 'to'}
              >
                {editing && (
                  <InputWithDropdown
                    {...rest}
                    defaultValue={query}
                    ref={ref}
                    key={`input-dropdown-${type}`}
                  />
                )}
              </List>
            )}
          </div>
        </div>
      </div>
    </div>
  )
})

RecipientSearch.propTypes = {
  /* assumes contacts are to be added by name, if it doesnt pass email validation */
  allowCreateNewContactByName: PropTypes.bool,

  // Allow mutiple recipients (enabled by default)
  allowMultipleRecipients: PropTypes.bool,

  // override for our bloody SUI override.
  bgColor: PropTypes.string, // eslint-disable-line react/no-unused-prop-types

  /** Called when a recipient is selected. */
  onAdd: PropTypes.func,

  /** Called when a recipient is removed. */
  onRemove: PropTypes.func,

  /** Called when the input is blurred, CAVEAT: if this fn unmounts this
   * component, it will stop the dropdown menu items' onClick handler, so may
   * need to be debounced/cancelable.
   * */
  onBlur: PropTypes.func,

  /** Called when the input is focused. Can be used to cancel a pending
   * onBlur. */
  onFocus: PropTypes.func,

  // The initial list of recipients
  recipients: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types

  // Tab index
  tabIndex: PropTypes.string,

  /* Used by the connected component */
  ticketId: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types

  // type of dropdown.
  type: PropTypes.oneOf(['to', 'cc', 'bcc']),

  // whether the search DD opens upward
  upward: PropTypes.bool,

  // Last search query
  query: PropTypes.string,

  /** Allows direct access to the search box input */
  searchBoxRef: PropTypes.func,
}

RecipientSearch.defaultProps = {
  bgColor: 'white',
  allowCreateNewContactByName: false,
  allowMultipleRecipients: true,
  onAdd: undefined,
  onBlur: undefined,
  onFocus: undefined,
  onRemove: undefined,
  type: undefined,
  tabIndex: '-1',
  upward: false,
  searchBoxRef: undefined,
}

export default RecipientSearch
